import { QueryObserverResult } from '@tanstack/react-query';
import { noop } from 'lodash-es';
import { createContext, PropsWithChildren } from 'react';
import { IListExpansion } from 'src/hooks/useListExpansion';
import {
  ActionOutboxEntityType,
  CatalogCounts,
  CatalogResults,
  EventWithData,
  Listing,
  PurchaseOrderTicketGroup,
  Sale,
} from 'src/WebApiController';

export const IDB_STORE_NAME = 'catalog-store';
export const EVENT_INDEX_DB_KEY_PREFIX = 'POS-Event-';
export const PERFORMER_INDEX_DB_KEY_PREFIX = 'POS-Performer-';
export const VENUE_INDEX_DB_KEY_PREFIX = 'POS-Venue-';
export const COUNTRIES_INDEX_DB_KEY = 'POS-Countries';

export const EmptyCatalogResults: CatalogResults = {
  events: {},
  performers: {},
  venues: {},
  viagIdToLatestIdLookup: {},
  posIdToViagIdLookup: {},
  countries: {},
  purchaseCount: 0,
};

export type EventWithDataMap = Record<string, EventWithData>;

export type EventIdWithRowVersion = {
  eventId: string;
  rowVer?: number | null;
  oldRowVer?: number | null;
};

export enum ExpandedCallbackStatus {
  Pending,
  InProgress,
  Done,
}

export type CatalogDataContextProviderProps<TQuery> = PropsWithChildren<{
  queryKey: string;
  doNotLogPageLoad?: boolean;
  entityType:
    | ActionOutboxEntityType.Listing
    | ActionOutboxEntityType.Sale
    | ActionOutboxEntityType.Purchase;
  /**
   * Use for catalog v2. If specified, this will replace the standard
   * getCatalogCountForSales/Listings/... query fn
   */
  getCatalogCountsCustom?: (filterQuery: TQuery) => Promise<CatalogCounts>;
  /**
   * Use for catalog v2. If specified,
   * this will replace the use of getEventsWithNonZeroCounts
   */
  getEventsWithNonZeroCountsCustom?: (
    entityType: ActionOutboxEntityType,
    data?: EventWithData[] | null,
    requireEventNotNull?: boolean
  ) => EventWithData[];
  getCatalogDataExpanded?: (
    ids: string[],
    filterQuery: TQuery
  ) => Promise<ExpandedEventData>;
  onCatalogDataExpanded?: (
    ids: string[],
    filterQuery: TQuery,
    data?: ExpandedEventData
  ) => Promise<ExpandedEventData>;
  transformEventData: (
    entityType:
      | ActionOutboxEntityType.Listing
      | ActionOutboxEntityType.Sale
      | ActionOutboxEntityType.TicketGroup
      | ActionOutboxEntityType.Purchase,
    data: EventWithData[],
    expandedEventData?: ExpandedEventData | null,
    isExpandedDataLoading?: boolean,
    filterZeroCounts?: boolean
  ) => EventWithData[];
  disabled?: boolean;
  disableAutoRefresh?: boolean;
  ignoreMaxCount?: boolean;
}>;

export type ICatalogDataContext = {
  isLoading: boolean;
  isItemsLoading: boolean;
  data?: CatalogResults;
  errorInfo?: { errorHeader: React.ReactNode; errorMessage: React.ReactNode };
  eventsTransformed?: EventWithData[];
  allEventIds?: {
    viagVirtId: string;
    performerId: number | null;
    venueId: number;
  }[];
  logKey: string;
  refreshCatalog: () => Promise<unknown>;
  eventsExpansion: IListExpansion<string>;
  updateItemInEvent: (
    item: Listing | Sale | PurchaseOrderTicketGroup,
    entityType: ActionOutboxEntityType,
    sectionalUpdate?: boolean
  ) => void;
  updateExpandedListItems: (updatedListingsByEvent: {
    [key: string]: Listing[];
  }) => void;
  refreshEventForItems: (
    itemIds: number[],
    entityType: ActionOutboxEntityType
  ) => void;
  /**
   * Only used for Purchase Flattened View Currently
   * Can be transformed to something more general such as `totalEntityCount` if needed
   */
  totalPurchaseCount?: number;
};

export type ExpandedEventData = {
  [key: string]: {
    sales: Sale[] | null;
    salesSummary: Sale | null;
    listings: Listing[] | null;
    listingsSummary: Listing | null;
    ticketGroups: PurchaseOrderTicketGroup[] | null;
    ticketGroupsSummary: PurchaseOrderTicketGroup | null;
    failedToRetrieveData: boolean;
    totalItemsCount?: number | null;
    /**
     * PIT ID for next page requests, null if not using PIT (Point-In-Time) pagination
     */
    pitId?: string | null;
  };
};
const emptyContext: ICatalogDataContext = {
  isLoading: false,
  isItemsLoading: false,
  data: undefined,
  eventsTransformed: undefined,
  allEventIds: undefined,
  logKey: '',
  refreshCatalog: () =>
    Promise.resolve({} as QueryObserverResult<unknown, unknown>),
  updateItemInEvent: noop,
  eventsExpansion: {
    expandedListItems: [],
    setExpandedListItems: () => {},
    toggleListItemExpansion: () => false,
    refreshExpandedListItems: () => Promise.resolve(),
    setListItemExpansion: () => {},
  },
  updateExpandedListItems: () => {},
  refreshEventForItems: () => {},
  totalPurchaseCount: undefined,
};

export const CatalogDataContextV2 =
  createContext<ICatalogDataContext>(emptyContext);
